import React, { memo, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    label: string;
  };
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, trend, icon, color = 'blue' }) => {
  const getTrendIcon = useMemo(() => {
    if (!trend) return null;

    if (trend.value > 0) {
      return <TrendingUp className='h-4 w-4 text-green-600' />;
    } else if (trend.value < 0) {
      return <TrendingDown className='h-4 w-4 text-red-600' />;
    } else {
      return <Minus className='h-4 w-4 text-gray-600' />;
    }
  }, [trend]);

  const getTrendColor = useMemo(() => {
    if (!trend) return 'text-gray-600';

    if (trend.value > 0) return 'text-green-600';
    if (trend.value < 0) return 'text-red-600';
    return 'text-gray-600';
  }, [trend]);

  const colorClasses = useMemo(() => {
    switch (color) {
      case 'green':
        return 'bg-green-50 border-green-200';
      case 'yellow':
        return 'bg-yellow-50 border-yellow-200';
      case 'red':
        return 'bg-red-50 border-red-200';
      case 'purple':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-secondary border-primary/30';
    }
  }, [color]);

  return (
    <div className='p-6 bg-white rounded-lg border border-gray-200 border-b-2 border-b-primary ' >
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <p className='text-sm font-medium text-gray-600 mb-1'>{title}</p>
          <p className='text-2xl font-bold text-gray-900'>{value}</p>
          {subtitle && <p className='text-sm text-gray-500 mt-1'>{subtitle}</p>}
          {trend && (
            <div className='flex items-center mt-2'>
              {getTrendIcon}
              <span className={`text-sm font-medium ml-1 ${getTrendColor}`}>
                {trend.value > 0 ? '+' : ''}
                {trend.value.toFixed(1)}% {trend.label}
              </span>
            </div>
          )}
        </div>
        {icon && <div className='flex-shrink-0 ml-4'>{icon}</div>}
      </div>
    </div>
  );
};

export default memo(MetricCard);
